import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star } from "lucide-react";
import { format } from "date-fns";
import { da } from "date-fns/locale";

interface ReviewsSectionProps {
  listingId: string;
}

interface Review {
  id: string;
  rating: number;
  text: string | null;
  created_at: string;
  bookings: {
    renter_id: string;
    users: {
      name: string | null;
    } | null;
  };
}

export default function ReviewsSection({ listingId }: ReviewsSectionProps) {
  const { data: reviews, isLoading } = useQuery({
    queryKey: ["listingReviews", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select(`
          id,
          rating,
          text,
          created_at,
          bookings!inner(
            renter_id,
            listing_id,
            users(name)
          )
        `)
        .eq("review_type", "guest_to_host")
        .eq("bookings.listing_id", listingId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Review[];
    },
    enabled: !!listingId,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Anmeldelser</h3>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border rounded-lg p-4 space-y-2">
              <div className="h-4 bg-muted rounded w-1/4"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!reviews?.length) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Anmeldelser</h3>
        <p className="text-muted-foreground">Ingen anmeldelser endnu</p>
      </div>
    );
  }

  const averageRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <h3 className="text-lg font-semibold">Anmeldelser</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <Star className="h-4 w-4 fill-warning text-warning mr-1" />
            <span className="font-medium">{averageRating.toFixed(1)}</span>
          </div>
          <span className="text-muted-foreground">({reviews.length} anmeldelse{reviews.length !== 1 ? 'r' : ''})</span>
        </div>
      </div>
      
      <div className="space-y-4">
        {reviews.map((review) => (
          <div key={review.id} className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-4 w-4 ${
                        star <= review.rating 
                          ? "fill-warning text-warning" 
                          : "text-muted-foreground"
                      }`}
                    />
                  ))}
                </div>
                <span className="font-medium">{review.bookings?.users?.name || "Anonym"}</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {format(new Date(review.created_at), "d. MMM yyyy", { locale: da })}
              </span>
            </div>
            
            {review.text && (
              <p className="text-foreground">{review.text}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}